import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * VideoTestimonials — two side-by-side autoplay-muted MP4 testimonials.
 *
 * Sora-2-generated clips live in /public/videos/.
 * Each card has a tap-to-unmute control and a quoted CTA caption overlaid.
 * Falls back to a static still + text quote if the video fails to load.
 */
const CLIPS = [
  {
    id: "casual",
    src: "/videos/testimonial_casual_selfie.mp4",
    poster: "/videos/testimonial_casual_poster.jpg",
    quote: "If your CV isn't getting callbacks, just try ResumeLab. It's free.",
    name: "Daniel O.",
    role: "Backend Engineer · Nairobi",
    accent: "from-[#EF4444] to-[#B91C1C]",
  },
  {
    id: "polished",
    src: "/videos/testimonial_polished_cafe.mp4",
    poster: "/videos/testimonial_polished_poster.jpg",
    quote: "Stop sending CVs into the void. ResumeLab tells you exactly what's wrong.",
    name: "Sarah M.",
    role: "Product Manager · Remote",
    accent: "from-[#002FA7] to-[#1E40AF]",
  },
];

function VideoCard({ clip, idx }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  // Autoplay on mount, pause when scrolled off-screen
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          v.play().catch(() => {});
          setPlaying(true);
        } else {
          v.pause();
          setPlaying(false);
        }
      },
      { threshold: 0.4 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      className="group relative overflow-hidden bg-zinc-950 aspect-video"
      data-testid={`video-testimonial-card-${clip.id}`}
    >
      {/* Gradient bg behind the video — visible behind letterboxing OR if the video
          fails to load (browser shows black; gradient covers via z-0) */}
      <div className={`absolute inset-0 bg-gradient-to-br ${clip.accent} z-0`} aria-hidden="true" />

      <video
        ref={ref}
        src={clip.src}
        poster={clip.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="relative z-10 w-full h-full object-cover"
        data-testid={`video-testimonial-player-${clip.id}`}
      />

      {/* Gradient overlay — improves caption legibility on busy frames */}
      <div className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-t from-zinc-950/85 via-zinc-950/20 to-transparent" />

      {/* Caption block (always visible) */}
      <div className="absolute inset-x-0 bottom-0 z-30 p-6 lg:p-8">
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/70 mb-3">
          ◆ TESTIMONIAL · {String(idx + 1).padStart(2, "0")}
        </div>
        <div className="font-display font-bold text-white text-lg lg:text-2xl leading-snug mb-3 max-w-md">
          &ldquo;{clip.quote}&rdquo;
        </div>
        <div className="flex items-center gap-3 text-white/80 text-xs">
          <span className="font-semibold text-white">{clip.name}</span>
          <span className="w-1 h-1 bg-white/40 rounded-full" />
          <span>{clip.role}</span>
        </div>
      </div>

      {/* Controls — top-right cluster */}
      <div className="absolute top-4 right-4 z-30 flex items-center gap-2">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? "Pause video" : "Play video"}
          className="w-9 h-9 grid place-items-center bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-colors text-white"
          data-testid={`video-toggle-play-${clip.id}`}
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Unmute video" : "Mute video"}
          className="w-9 h-9 grid place-items-center bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-colors text-white"
          data-testid={`video-toggle-mute-${clip.id}`}
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function VideoTestimonials() {
  return (
    <section
      className="border-b border-zinc-200 bg-white"
      data-testid="video-testimonials-section"
      aria-labelledby="video-testimonials-heading"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-8 lg:mb-10">
          <div className="lg:col-span-7">
            <div className="overline mb-4">◷ WHAT USERS SAY</div>
            <h2
              id="video-testimonials-heading"
              className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight leading-[1.05] text-zinc-900"
            >
              Real people.<br />
              Real callbacks.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3">
            <p className="text-zinc-600 text-base lg:text-lg leading-relaxed">
              Two of the thousands of audits that turned silent inboxes into interview calendars.
              No scripts, no influencer deals — just people whose resumes finally got read.
            </p>
          </div>
        </div>

        {/* Two-up grid — stacks on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-testid="video-testimonials-grid">
          {CLIPS.map((clip, i) => (
            <VideoCard key={clip.id} clip={clip} idx={i} />
          ))}
        </div>

        {/* CTA strip */}
        <div className="mt-12 lg:mt-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pt-8 border-t border-zinc-200">
          <p className="text-sm text-zinc-600 max-w-md">
            <strong className="text-zinc-900">Your audit is free.</strong>{" "}
            No signup. No card. 12 seconds to find out what&apos;s blocking your callbacks.
          </p>
          <Link
            to="/evaluate"
            className="inline-flex items-center gap-2 bg-[#EF4444] hover:bg-[#DC2626] text-white px-6 py-3 text-sm font-semibold transition-colors"
            data-testid="video-testimonials-cta"
          >
            Audit my resume free
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
